<template>
  <div class="chart-widget">
    <div class="widget-header">
      <div class="controls-row">
        <select v-if="editable && canChangeType && viewMode === 'chart'" v-model="selectedType" class="type-selector">
          <option value="bar">Bar</option>
          <option value="line">Line</option>
          <option value="area">Area</option>
        </select>
        <button class="toggle-btn" @click="viewMode = viewMode === 'chart' ? 'table' : 'chart'">
          {{ viewMode === 'chart' ? t('viewTable', lang) : t('viewChart', lang) }}
        </button>
        <button v-if="viewMode === 'table'" class="export-btn" @click="exportToExcel">
          {{ t('exportXlsx', lang) }}
        </button>
      </div>
    </div>

    <div v-if="viewMode === 'chart'" class="chart-container" :style="containerStyle">
      <apexchart
        :type="selectedType"
        :options="localOptions"
        :series="series"
        :height="chartHeight"
        @dataPointSelection="onDataPointSelection"
      ></apexchart>
    </div>

    <div v-else class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="col in tableColumns" :key="col">{{ col }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in tableData" :key="index">
            <td v-for="col in tableColumns" :key="col + index">{{ row[col] }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import * as XLSX from 'xlsx';
import { t } from '../i18n/index.js';

const props = defineProps({
  options: { type: Object, required: true },
  series: { type: Array, required: true },
  allowTypeChange: { type: Boolean, default: false },
  editable: { type: Boolean, default: true },
  lang: { type: String, default: 'ca_es' }
});

const emit = defineEmits(['data-point-selection']);

const selectedType = ref(props.options.chart.type);
const viewMode = ref('chart');
const allowedTypes = ['bar', 'line', 'area'];

const canChangeType = computed(() => {
  if (props.allowTypeChange !== null) return props.allowTypeChange;
  return allowedTypes.includes(props.options.chart.type);
});

const localOptions = computed(() => {
  // Shallow merge per preservar les funcions (formatters) que JSON.stringify destruiria.
  const opts = props.options;
  return {
    ...opts,
    chart: {
      ...(opts.chart || {}),
      type: selectedType.value,
      toolbar: { show: false },
      zoom: { enabled: false }
    }
  };
});

const tableData = computed(() => {
  if (props.options.chart.type === 'pie' || props.options.chart.type === 'donut') {
    return props.series.map((val, i) => ({
      Category: props.options.labels && props.options.labels[i] ? props.options.labels[i] : `Item ${i+1}`,
      Value: val
    }));
  } else {
    return (props.options.xaxis?.categories || []).map((cat, i) => {
      const row = { Category: cat };
      props.series.forEach(s => {
        row[s.name || 'Value'] = s.data[i];
      });
      return row;
    });
  }
});

const tableColumns = computed(() => {
  if (!tableData.value || tableData.value.length === 0) return [];
  return Object.keys(tableData.value[0]);
});

const chartHeight = computed(() => {
  const opts = props.options;
  if (opts.chart?.heightPerCategory) {
    const count = opts.xaxis?.categories?.length || props.series?.[0]?.data?.length || 0;
    return Math.max(opts.chart?.minHeight || 300, count * opts.chart.heightPerCategory);
  }
  return opts.chart?.height || 450;
});

const containerStyle = computed(() => {
  const opts = props.options;
  const max = opts.maxHeight || (typeof opts.chart?.height === 'number' ? opts.chart.height : null);
  if (!max) return { height: chartHeight.value + 'px' };
  const maxPx = typeof max === 'number' ? max : parseInt(max, 10) || 0;
  const needsScroll = maxPx > 0 && chartHeight.value > maxPx;
  return {
    height: typeof max === 'number' ? `${max}px` : max,
    overflowY: needsScroll ? 'auto' : 'hidden',
    overflowX: 'hidden'
  };
});

watch(() => props.options.chart.type, (newType) => {
  if (canChangeType.value) {
    selectedType.value = newType;
  }
});

function onDataPointSelection(event, chartContext, config) {
  emit('data-point-selection', config);
}

function exportToExcel() {
  const ws = XLSX.utils.json_to_sheet(tableData.value);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");

  const title = props.options.title?.text || 'chart_data';
  const safeTitle = title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  XLSX.writeFile(wb, `${safeTitle}.xlsx`);
}
</script>

<style scoped>
.chart-widget {
  background: white;
  border-radius: 4px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  border: 1px solid #e8e8e8;
  position: relative;
  overflow-x: hidden;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.chart-container {
  width: 100%;
}

.chart-widget:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
}

.widget-header {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
}

.type-selector {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  font-size: 0.875rem;
  color: #374151;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
}

.type-selector:hover,
.type-selector:focus {
  border-color: #3b82f6;
}

.controls-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.toggle-btn,
.export-btn {
  padding: 3px 10px;
  border-radius: 3px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  font-size: 0.78rem;
  color: #555;
  outline: none;
  cursor: pointer;
  transition: all 0.15s;
  font-family: 'Source Sans Pro', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.toggle-btn:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.export-btn {
  background-color: #10b981;
  color: white;
  border-color: #059669;
}

.export-btn:hover {
  background-color: #059669;
  border-color: #047857;
}

.table-container {
  max-height: 450px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 10px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table th,
.data-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
  position: sticky;
  top: 0;
}

.data-table tr:hover {
  background-color: #f9fafb;
}
</style>
